/**
 * Pure helpers behind the UT account screens: the 4-segment password
 * strength meter, the confirm-password match line and the initials on the
 * display-name preview card.
 */

export type StrengthScore = 0 | 1 | 2 | 3 | 4;

export const STRENGTH_LABELS: Record<StrengthScore, string> = {
  0: 'Type a password',
  1: 'Too short',
  2: 'OK',
  3: 'Strong',
  4: 'Very strong',
};

/**
 * 0 = empty, 1 = shorter than `min`, 2 = OK, 3 = 10+ characters, plus one
 * more for mixing in a digit and a symbol. `min` is the screen's own minimum
 * (6 for a reset, 8 for changing it from the profile).
 */
export function passwordScore(pw: string, min = 6): StrengthScore {
  const n = pw.length;
  if (n === 0) return 0;
  if (n < min) return 1;
  const base = n < 10 ? 2 : 3;
  const mixed = /[0-9]/.test(pw) && /[^a-z0-9]/i.test(pw) ? 1 : 0;
  return Math.min(base + mixed, 4) as StrengthScore;
}

export type MatchState = 'empty' | 'match' | 'mismatch';

/** State of the confirm field: nothing typed yet, matching, or not. */
export function matchState(pw: string, confirm: string): MatchState {
  if (!confirm) return 'empty';
  return confirm === pw ? 'match' : 'mismatch';
}

/** Long enough and confirmed: the only time the save button lights up. */
export function passwordReady(pw: string, confirm: string, min = 6): boolean {
  return pw.length >= min && matchState(pw, confirm) === 'match';
}

/** Up to two initials, uppercase; "?" when there's nothing to go on. */
export function nameInitials(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2);
  return (letters || '?').toUpperCase();
}

/** Longest display name the UT name sheet accepts (the longest in use is 10). */
export const DISPLAY_NAME_MAX = 20;

/** Names no player may take. */
const RESTRICTED_NAMES = ['admin', 'administrator'];

/** Why a display name can't be saved, or null when it's fine. */
export function displayNameProblem(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Enter a name.';
  if (n.length > DISPLAY_NAME_MAX) return `Keep it to ${DISPLAY_NAME_MAX} characters.`;
  if (RESTRICTED_NAMES.includes(n.toLowerCase())) return 'That name is reserved.';
  return null;
}
